import { t } from "@lingui/core/macro";
import {
  arrayOrderBy,
  EMPTY_ARRAY,
  getEmailDomain,
  isValidEmail,
  KeyNames,
  useConfirmationDialog,
} from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import { FeatureFlagKey } from "@regrello/feature-flags-api";
import {
  type RoleFields,
  UserAccessLevel,
  useCurrentTenantQuery,
  useIsEmailUniqueQueryLazyQuery,
  useRolesQuery,
} from "@regrello/graphql-api";
import { LANG_MAP, type LanguageCode, SUPPORTED_LOCALES, TARGET_LANG_MAP } from "@regrello/i18n-llm-translator";
import { RegrelloConfirmationDialog, RegrelloIntent, RegrelloTooltip } from "@regrello/ui-core";
import React, { useCallback, useMemo, useState } from "react";
import { type SetValueConfig, type UseFormReturn, useWatch } from "react-hook-form";

import { ValidationRules } from "../../../../../constants/globalConstants";
import { FeatureFlagService } from "../../../../../services/FeatureFlagService";
import type { UserTheme } from "../../../../../state/applicationState";
import { RegrelloFormValidationReason } from "../../../../../utils/formUtils";
import { useErrorHandler } from "../../../../../utils/hooks/useErrorHandler";
import { useMemoizedAsyncLoadedValue } from "../../../../../utils/hooks/useMemoizedAsyncLoadedValue";
import { getUserAccessLevelDisplayString } from "../../../../../utils/userUtils";
import { useUser } from "../../../../app/authentication/userContextUtils";
import { RegrelloPermissions } from "../../../../app/authorization/permissions";
import { RegrelloControlledFormFieldText } from "../../../../molecules/formFields/controlled/RegrelloControlledFormFieldText";
import {
  RegrelloControlledFormFieldMultiSelect,
  RegrelloControlledFormFieldRadioGroup,
  RegrelloControlledFormFieldSelect,
  RegrelloControlledFormFieldTagInput,
} from "../../../../molecules/formFields/controlled/regrelloControlledFormFields";
import { RegrelloFormFieldSwitch } from "../../../../molecules/formFields/RegrelloFormFieldSwitch";
import { type OutOfOfficeEventFormFields, RegrelloOutOfOfficeEventForm } from "./RegrelloOutOfOfficeEventForm";

// (zstanik): Wide enough for the static label "Permission level" to fit on one line.
const LABEL_WIDTH = 120;

// eslint-disable-next-line react-refresh/only-export-components
export enum FrontendUserAccessLevel {
  ADMIN = "ADMIN",
  NON_ADMIN_INTERNAL = "NON_ADMIN_INTERNAL",
  NON_ADMIN_EXTERNAL = "NON_ADMIN_EXTERNAL",
}

export interface UserFormFields {
  name: string;
  emailContext:
    | {
        type: "single";
        email: string;
      }
    | {
        type: "multiple";
        emails: string[];
      };
  accessLevelPermissionsV2: UserAccessLevel;
  /** Only used by permissions V2 currently. */
  permissionsV2Roles: RoleFields[];
  outOfOffice: OutOfOfficeEventFormFields | undefined;
  userTheme?: UserTheme;
  language?: LanguageCode;
}

export interface RegrelloUserFormProps {
  form: UseFormReturn<UserFormFields>;

  /**
   * Provide `true` when the form is for the `currentUser` in order to prevent specific self-actions
   * from being taken (e.g., removing own admin role).
   *
   * @default false
   */
  isUserEditingThemselves?: boolean;

  /**
   * Whether the name field is disabled.
   *
   * @default false
   */
  isNameDisabled?: boolean;

  /**
   * Whether the user's email address can be changed.
   *
   * @default false
   */
  isEmailEditable?: boolean;

  /**
   * Hide out of office toggle.
   *
   * @default false
   */
  isOooSettingsHidden: boolean;

  setTriedToSubmitWithoutDelegates?: React.Dispatch<React.SetStateAction<boolean>>;
  triedToSubmitWithoutDelegates?: boolean;

  /**
   * Whether the user's type field is disabled.
   *
   * @default false
   */
  isTypeDisabled?: boolean;
}

/**
 * Renders a standard form for collecting information about a user. Can be used in add-user or
 * edit-user dialogs.
 */
export const RegrelloUserForm = React.memo(function RegrelloUserFormFn({
  form,
  isOooSettingsHidden,
  isEmailEditable,
  isNameDisabled,
  isTypeDisabled,
  isUserEditingThemselves = false,
  setTriedToSubmitWithoutDelegates,
  triedToSubmitWithoutDelegates,
}: RegrelloUserFormProps) {
  const { currentUser } = useUser();
  const isExternal = !RegrelloPermissions.User.isInternal(currentUser);
  const { handleError } = useErrorHandler();

  const isI18nEnabled = FeatureFlagService.isEnabled(FeatureFlagKey.I18N_PHASE_ONE_2024_10);
  const isLimitedUserFeatureFlagEnabled = FeatureFlagService.isEnabled(FeatureFlagKey.LIMITED_USER_2025_02);

  const { data: currentTenant } = useCurrentTenantQuery({
    fetchPolicy: "cache-and-network",
  });

  const emailContextType = useWatch({ control: form.control, name: "emailContext.type" });

  const outOfOffice = useWatch({ control: form.control, name: "outOfOffice" });

  // (akager): Avoid errors of the form 'Type instantiation is excessively deep and possibly
  // infinite' by explicitly stating the keys instead of reling on the default Path type.
  const formSetValue = form.setValue as <K extends keyof UserFormFields>(
    name: K,
    value: UserFormFields[K],
    options?: SetValueConfig,
  ) => void;

  const onSwitchChange = useCallback(() => {
    if (outOfOffice === undefined) {
      // eslint-disable-next-line lingui/no-unlocalized-strings
      formSetValue("outOfOffice", { startAt: null, endAt: null, delegationSettings: undefined });
    } else {
      // eslint-disable-next-line lingui/no-unlocalized-strings
      formSetValue("outOfOffice", undefined);
      if (setTriedToSubmitWithoutDelegates != null) {
        setTriedToSubmitWithoutDelegates(false);
      }
    }
  }, [outOfOffice, formSetValue, setTriedToSubmitWithoutDelegates]);

  const isEmailInUserDomain = useCallback(
    (email: string) => getEmailDomain(email) === getEmailDomain(currentUser.email),
    [currentUser.email],
  );

  const selectedAccessLevelPermissionsV2 = useWatch({ control: form.control, name: "accessLevelPermissionsV2" });
  const selectedRoles = useWatch({ control: form.control, name: "permissionsV2Roles" });
  const canAssignRoles = currentUser.permissions.canAssignRoles;
  // (wsheehan): We want to hide roles when the current user is inviting someone but does not have
  // access to assign roles.
  const isRolesDropdownVisible =
    canAssignRoles || (selectedRoles != null && selectedRoles.length > 0) || isUserEditingThemselves;

  const isPermissionsV2Enabled = FeatureFlagService.isEnabled(FeatureFlagKey.PERMISSIONS_V2_2024_01);
  const rolesQueryResult = useRolesQuery({
    variables: {
      input: {}, // No need to preload role member parties here.
    },
    fetchPolicy: "no-cache",
    skip: !isPermissionsV2Enabled,
  });

  const asyncPermissionV2Roles = useMemoizedAsyncLoadedValue(rolesQueryResult, (d) => d.roles);

  const permissionV2Roles: RoleFields[] = useMemo(
    () =>
      arrayOrderBy(
        (asyncPermissionV2Roles.type !== "loaded" ? EMPTY_ARRAY : asyncPermissionV2Roles.value.roles)
          .map(({ role }) => role)
          .filter((role) => {
            // Always show custom roles, and only show internal vs external system roles based on
            // the selected access level.
            return !role.isSystem || selectedAccessLevelPermissionsV2 === UserAccessLevel.INTERNAL;
          }),
        ["name"],
        ["asc"],
      ),
    [asyncPermissionV2Roles, selectedAccessLevelPermissionsV2],
  );

  const {
    isOpen: isConfirmTypeSwitchDialogOpen,
    open: openConfirmTypeSwitchDialog,
    close: closeConfirmTypeSwitchDialog,
  } = useConfirmationDialog();

  const onConfirmTypeSwitchDialogCancel = useCallback(() => {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    formSetValue("accessLevelPermissionsV2", UserAccessLevel.INTERNAL);
    closeConfirmTypeSwitchDialog();
  }, [closeConfirmTypeSwitchDialog, formSetValue]);

  const onAccessLevelInternalToExternalConfirm = useCallback(() => {
    const newRoles = form.getValues().permissionsV2Roles.filter((r) => !r.isSystem);
    // eslint-disable-next-line lingui/no-unlocalized-strings
    formSetValue("permissionsV2Roles", newRoles);
    closeConfirmTypeSwitchDialog();
  }, [closeConfirmTypeSwitchDialog, form, formSetValue]);

  const {
    isOpen: isConfirmTypeSwitchToLimitedDialogOpen,
    open: openConfirmTypeSwitchToLimitedDialog,
    close: closeConfirmTypeSwitchToLimitedDialog,
  } = useConfirmationDialog();

  const [previousAccessLevel, setPreviousAccessLevel] = useState(form.getValues("accessLevelPermissionsV2"));

  const onConfirmTypeSwitchToLimitedDialogCancel = useCallback(() => {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    formSetValue("accessLevelPermissionsV2", previousAccessLevel);
    closeConfirmTypeSwitchToLimitedDialog();
  }, [closeConfirmTypeSwitchToLimitedDialog, formSetValue, previousAccessLevel]);

  const onAccessLevelToLimitedConfirm = useCallback(() => {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    formSetValue("permissionsV2Roles", EMPTY_ARRAY);
    closeConfirmTypeSwitchToLimitedDialog();
  }, [closeConfirmTypeSwitchToLimitedDialog, formSetValue]);

  const getSelectedItemProps = useCallback(
    (option: RoleFields, _index: number) => {
      const isLikelyAdminRole = option.isSystem && option.name === "Admin";
      const isScimRole = option.scimId != null && option.scimId.length > 0;

      return {
        icon: { type: "iconName" as const, iconName: getAccessRoleIcon(option) },
        children: option.name,
        disabled: isScimRole || (isLikelyAdminRole && isUserEditingThemselves),
        intent: RegrelloIntent.NEUTRAL,
      };
    },
    [isUserEditingThemselves],
  );

  const onAccessLevelValueChange = useCallback(
    (_: unknown, newValue: UserAccessLevel | null, oldValue: UserAccessLevel | null) => {
      if (oldValue != null) {
        setPreviousAccessLevel(oldValue);
      }

      const newRoles = form.getValues().permissionsV2Roles;
      if (newRoles.length > 0 && newValue === UserAccessLevel.LIMITED) {
        openConfirmTypeSwitchToLimitedDialog();

        return;
      }
      if (
        newRoles.some((r) => r.isSystem) &&
        oldValue === UserAccessLevel.INTERNAL &&
        newValue !== UserAccessLevel.INTERNAL
      ) {
        openConfirmTypeSwitchDialog();
      }
    },
    [form, openConfirmTypeSwitchDialog, openConfirmTypeSwitchToLimitedDialog],
  );

  const getItemDisabled = useCallback(
    (option: RoleFields) => {
      const isLikelyAdminRole = option.isSystem && option.name === "Admin";
      const isScimRole = option.scimId != null && option.scimId.length > 0;
      return isScimRole || (isLikelyAdminRole && isUserEditingThemselves);
    },
    [isUserEditingThemselves],
  );

  const renderItem = useCallback((option: RoleFields) => {
    return {
      key: option.id,
      icon: getAccessRoleIcon(option),
      text: option.name,
    };
  }, []);

  const [getIsEmailUniqueAsync] = useIsEmailUniqueQueryLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      handleError(error);
    },
  });

  /**
   * Callback that returns `true` if another loaded user is already attached to the provided email.
   * If so, then we shouldn't permit another user to be created with that email, because it would
   * error on the backend.
   */
  const isEmailUnique = useCallback(
    async (email: string) => {
      // If the current user is external, always return true without making the query
      if (isExternal) {
        return true;
      }
      const result = await getIsEmailUniqueAsync({
        variables: {
          email,
        },
      });

      return result.data?.isEmailUnique.isUnique ?? false;
    },
    [getIsEmailUniqueAsync, isExternal],
  );

  const validateEmailsAreUnique = useCallback(
    async (emails: string[], validationMessage: string): Promise<undefined | string> => {
      const allUnique = await emails.reduce<Promise<boolean>>(async (accPromise: Promise<boolean>, email: string) => {
        const acc = await accPromise;
        const unique = await isEmailUnique(email);
        return acc && unique;
      }, Promise.resolve(true));

      return allUnique ? undefined : validationMessage;
    },
    [isEmailUnique],
  );

  const THEME_RADIO_OPTIONS: Array<{ label: string; value: UserTheme }> = [
    { label: t`Light`, value: "light" },
    { label: t`Dark (beta)`, value: "dark" },
    { label: t`System (beta)`, value: "system" },
  ];

  const accessLevelOptions = isLimitedUserFeatureFlagEnabled
    ? currentUser.accessLevel === UserAccessLevel.EXTERNAL
      ? [UserAccessLevel.EXTERNAL, UserAccessLevel.LIMITED]
      : [UserAccessLevel.INTERNAL, UserAccessLevel.EXTERNAL, UserAccessLevel.LIMITED]
    : currentUser.accessLevel === UserAccessLevel.EXTERNAL
      ? [UserAccessLevel.EXTERNAL]
      : [UserAccessLevel.INTERNAL, UserAccessLevel.EXTERNAL];

  // Disable editing roles when OOO delegations are editable to avoid bugs around keeping the form
  // consistent. Limited users cannot receive roles.
  const isRolesDropdownDisabled =
    !canAssignRoles ||
    selectedAccessLevelPermissionsV2 === UserAccessLevel.LIMITED ||
    (!isOooSettingsHidden && outOfOffice?.delegationSettings != null);

  const rolesDropdownTooltipText =
    selectedAccessLevelPermissionsV2 === UserAccessLevel.LIMITED
      ? t`Limited users cannot be assigned roles`
      : !isOooSettingsHidden && outOfOffice?.delegationSettings != null
        ? t`You cannot edit your own roles and out of office delegations simultaneously. Please edit your user's roles in the Admin tab instead.`
        : undefined;

  return (
    <div className="flex flex-col gap-2">
      {emailContextType === "multiple" ? (
        // Emails
        <RegrelloControlledFormFieldTagInput
          autoFocus={false}
          controllerProps={{
            control: form.control,
            name: "emailContext.emails",
            rules: {
              ...ValidationRules.REQUIRED,
              validate: isEmailEditable
                ? {
                    [RegrelloFormValidationReason.PATTERN]: (value) =>
                      value.map((token) => token.trim()).every(isValidEmail)
                        ? !currentTenant?.currentTenant?.permissionAllowNonAdminUserToInviteNonDomain &&
                          !RegrelloPermissions.User.isInternal(currentUser)
                          ? value.reduce((acc, email) => acc && isEmailInUserDomain(email), true)
                            ? undefined
                            : t`You don’t have permission to add a user who is outside of your organization. Please contact a Regrello admin to add a person from another domain.`
                          : undefined
                        : t`Email(s) are invalid`,
                    [RegrelloFormValidationReason.UNIQUE]: (value) =>
                      validateEmailsAreUnique(value, t`One or more emails are already associated with an account.`),
                  }
                : undefined,
            },
          }}
          dataTestId={DataTestIds.ADD_USER_DIALOG_FIELD_MULTIPLE_EMAILS}
          delimiters={[KeyNames.COMMA, KeyNames.SPACE, KeyNames.ENTER]}
          disabled={!isEmailEditable}
          helperText={
            isPermissionsV2Enabled
              ? t`Enter email addresses separated by commas or spaces.`
              : t`Type an email, then press enter, space, or comma to add your input.`
          }
          isRequiredAsteriskShown={true}
          label={t`Emails`}
          labelWidth={LABEL_WIDTH}
        />
      ) : (
        // Emails
        <>
          <RegrelloControlledFormFieldText
            autoFocus={true}
            controllerProps={{
              control: form.control,
              name: "name",
              rules: ValidationRules.REQUIRED,
            }}
            dataTestId={DataTestIds.ADD_USER_DIALOG_FIELD_NAME}
            disabled={isNameDisabled}
            isRequiredAsteriskShown={true}
            label={t`Name`}
            labelWidth={LABEL_WIDTH}
          />
          <RegrelloControlledFormFieldText
            controllerProps={{
              control: form.control,
              name: "emailContext.email",
              rules: {
                ...ValidationRules.REQUIRED,
                validate: isEmailEditable
                  ? {
                      [RegrelloFormValidationReason.PATTERN]: (value) =>
                        isValidEmail(value)
                          ? !RegrelloPermissions.User.isInternal(currentUser)
                            ? isEmailInUserDomain(value)
                              ? undefined
                              : t`You don’t have permission to add a user who is outside of your organization. Please contact a Regrello admin to add a person from another domain.`
                            : undefined
                          : t`Email is invalid`,
                      [RegrelloFormValidationReason.UNIQUE]: (value) =>
                        validateEmailsAreUnique([value], t`This email is already associated with another account.`),
                    }
                  : undefined,
              },
            }}
            dataTestId={DataTestIds.ADD_USER_DIALOG_FIELD_EMAIL}
            disabled={!isEmailEditable}
            isRequiredAsteriskShown={true}
            label={t`Email`}
            labelWidth={LABEL_WIDTH}
          />
        </>
      )}
      {isPermissionsV2Enabled ? (
        <>
          {/* Type */}
          <RegrelloControlledFormFieldSelect
            controllerProps={{
              control: form.control,
              name: "accessLevelPermissionsV2",
              rules: ValidationRules.REQUIRED,
            }}
            dataTestId={DataTestIds.ADD_USER_DIALOG_FIELD_ACCESS_TYPE}
            disabled={isUserEditingThemselves || isTypeDisabled}
            getOptionLabel={(accessLevel) => getUserAccessLevelDisplayString({ accessLevel })}
            helperText={t`Use Internal for anyone belonging to your organization.`}
            isFilterable={false}
            isRequiredAsteriskShown={true}
            label={t`Type`}
            labelWidth={LABEL_WIDTH}
            onValueChange={onAccessLevelValueChange}
            options={accessLevelOptions}
          />

          {/* Roles */}
          {isRolesDropdownVisible && (
            <RegrelloTooltip content={rolesDropdownTooltipText} side="top">
              <span>
                <RegrelloControlledFormFieldMultiSelect
                  controllerProps={{
                    control: form.control,
                    name: "permissionsV2Roles",
                  }}
                  dataTestId={DataTestIds.ADD_USER_DIALOG_FIELD_ACCESS_CUSTOM_ROLES}
                  disabled={isRolesDropdownDisabled}
                  getItemDisabled={getItemDisabled}
                  getSelectedItemProps={getSelectedItemProps}
                  helperText={t`Roles grant access to blueprints, workflows, and other data in this workspace. Select one or multiple.`}
                  isItemsEqual={(a, b) => a.id === b.id}
                  isRequiredAsteriskShown={false}
                  itemPredicate={itemPredicate}
                  items={permissionV2Roles}
                  label={t`Roles`}
                  labelWidth={LABEL_WIDTH}
                  renderItem={renderItem}
                />
              </span>
            </RegrelloTooltip>
          )}

          <RegrelloConfirmationDialog
            content={t`Changing a user from internal to external is a destructive change. The user will have any workspace-level role removed from their profile. Ensure this is what you want to do before saving.`}
            isOpen={isConfirmTypeSwitchDialogOpen}
            onClose={onConfirmTypeSwitchDialogCancel}
            onConfirm={onAccessLevelInternalToExternalConfirm}
            title={t`Are you sure?`}
          />
          <RegrelloConfirmationDialog
            content={t`Changing a user to limited is a destructive change. The user will have all roles removed from their profile. Ensure this is what you want to do before saving.`}
            isOpen={isConfirmTypeSwitchToLimitedDialogOpen}
            onClose={onConfirmTypeSwitchToLimitedDialogCancel}
            onConfirm={onAccessLevelToLimitedConfirm}
            title={t`Are you sure?`}
          />
        </>
      ) : null}
      {/* Language */}
      {isI18nEnabled && (
        <RegrelloControlledFormFieldSelect
          controllerProps={{
            control: form.control,
            name: "language",
          }}
          getOptionLabel={(languageCode: LanguageCode) => {
            const targetLang = TARGET_LANG_MAP[languageCode];
            const englishLang = languageCode.startsWith("en") ? "" : `(${LANG_MAP[languageCode]})`;
            return `${targetLang} ${englishLang}`;
          }}
          label={t`Language`}
          labelWidth={LABEL_WIDTH}
          options={SUPPORTED_LOCALES as unknown as LanguageCode[]}
          renderOption={(languageCode: LanguageCode) => {
            return (
              <span>
                {TARGET_LANG_MAP[languageCode]} <span className="text-textMuted">({LANG_MAP[languageCode]})</span>
              </span>
            );
          }}
        />
      )}
      {/* Theme */}
      {form.getValues("userTheme") != null && (
        <RegrelloControlledFormFieldRadioGroup
          controllerProps={{
            control: form.control,
            name: "userTheme",
            rules: ValidationRules.REQUIRED,
          }}
          label={t`Theme`}
          labelWidth={LABEL_WIDTH}
          options={THEME_RADIO_OPTIONS}
        />
      )}
      {/* Out of Office */}
      {!isOooSettingsHidden && (
        <div>
          <RegrelloFormFieldSwitch
            checked={outOfOffice != null}
            dataTestId={DataTestIds.OUT_OF_OFFICE_TOGGLE}
            label={t`Out of office`}
            labelWidth={LABEL_WIDTH}
            onChange={onSwitchChange}
            secondaryLabel={t`Set as away`}
          />
          <div className="ml-32">
            {outOfOffice != null &&
              triedToSubmitWithoutDelegates != null &&
              setTriedToSubmitWithoutDelegates != null && (
                <RegrelloOutOfOfficeEventForm
                  form={form}
                  setTriedToSubmitWithoutDelegates={setTriedToSubmitWithoutDelegates}
                  triedToSubmitWithoutDelegates={triedToSubmitWithoutDelegates}
                />
              )}
          </div>
        </div>
      )}
    </div>
  );
});

function getAccessRoleIcon(option: RoleFields) {
  return option.isSystem ? ("custom-role-admin-field" as const) : ("role" as const);
}

function itemPredicate(query: string, item: RoleFields) {
  return item.name.toLocaleLowerCase().includes(query.toLocaleLowerCase());
}
